import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { player } from 'src/client/player';
import { useWindowSize } from 'src/common/hooks/useWindowSize';
import { useStores } from 'src/common/providers';
import { selectIsMobile } from 'src/common/selectors/app';
import { isSmall, isXSmall } from 'src/common/utils/breakpoints';
import { mintSingleton } from '../../../client/mint';
import { selectVideoAdDialogState } from '../../selectors/dialog';
import cssVars from '../../styles/variables';
import CommonDialog from '../shared/dialog/CommonDialog';
import VideoAdViewContainer from './VideoAdViewContainer';
import css from './videoAd.module.scss';

// NOTE: DEFAULT_WIDTH & DEFAULT_HEIGHT mirror the largest video ad format sent as an option in the adRequest params
const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 480;
const AD_WIDTH_400 = 400;
const AD_WIDTH_315 = 315;
const AD_HEIGHT_350 = 350;

const clampHeight = (targetHeight, maxHeight) =>
  maxHeight > 0 ? Math.min(targetHeight, maxHeight) : targetHeight;

const getTargetAdSize = (breakpoint, maxHeight) => {
  if (isSmall(breakpoint)) {
    return {
      width: DEFAULT_WIDTH,
      height: clampHeight(DEFAULT_HEIGHT, maxHeight),
    };
  }

  if (isXSmall(breakpoint)) {
    return {
      width: AD_WIDTH_400,
      height: clampHeight(AD_HEIGHT_350, maxHeight),
    };
  }

  return {
    width: AD_WIDTH_315,
    height: clampHeight(AD_HEIGHT_350, maxHeight),
  };
};

export default function VideoAdDialog({ breakpoint }) {
  const { videoHeight = DEFAULT_HEIGHT } = useSelector(selectVideoAdDialogState);
  const isMobile = useSelector(selectIsMobile);
  const isOpen = useStores().playerStore.isVideoAdLoaded();
  const [width, height] = useWindowSize();
  // NOTE: 135px accounts for the height of the dialog title and padding ensuring the ad fits within the viewport
  // This handles mobile horizontal view
  const failsafeHeight = height - 135;

  useEffect(() => {
    if (!isOpen || !width || !height || !player.adContainerElement?.clientWidth) {
      return;
    }

    const currentWidth = player.adContainerElement.clientWidth;
    const currentHeight = player.adContainerElement.clientHeight;
    const { width: targetWidth, height: targetHeight } = getTargetAdSize(
      breakpoint,
      failsafeHeight,
    );

    const needsResize = currentWidth !== targetWidth || currentHeight > targetHeight;
    if (needsResize) {
      player.resizeVideoAd(targetWidth, targetHeight);
    }
  }, [breakpoint, isOpen, width, height, player.adContainerElement?.clientWidth]);

  useEffect(() => {
    if (isOpen) {
      mintSingleton.instance?.destroyAllDisplayAds();
    } else {
      mintSingleton.instance?.enableDisplayAds();
    }
  }, [isOpen]);

  return (
    <CommonDialog
      classNames={{
        content: clsx(css.videoAdDialogContent, {
          [css.videoAdDialogContentRegular]: !isSmall(breakpoint),
          [css.videoAdDialogContentSmall]: isSmall(breakpoint),
        }),
        body: clsx(css.videoAdDialogBody, {
          [css.videoAdDialogBodyMobile]: isMobile,
        }),
        // We keep the dialog rendered but not visible - so the ad library can find and load the ad into the container.
        portal: !isOpen && css.dialogPortalHidden,
      }}
      zIndex={cssVars['--above-top-z-index']}
      dialogOpen
      hideCloseButton
      variant="wide"
      fullScreen={isMobile}
      data-testid="videoAdDialog"
    >
      <VideoAdViewContainer breakpoint={breakpoint} height={videoHeight} />
    </CommonDialog>
  );
}

VideoAdDialog.propTypes = {
  breakpoint: PropTypes.number.isRequired,
};
